import { MessageBus } from '@bs/utils';
import { ARIA, CLASS_NAMES, KEY_CODES, SELECTORS, EVENTS } from '@bs/constants';

/**
 * A class for the Header Navigation component
 */
export default class HeaderNavigation extends MessageBus {
  constructor(element) {
    super();
    this.element = element;
    this.searchDebounceTimer = null;
    this.scrollPosition = 0;

    // Timer used to add a small "hover intent" delay before closing a
    // desktop dropdown, so the cursor can travel across the gap between
    // the toggle and the dropdown panel without the panel flickering shut.
    this.dropdownCloseTimer = null;
    this.dropdownHideTimer = null;

    // Only wire up hover behaviour on devices that actually have a fine,
    // hover-capable pointer (i.e. a mouse/trackpad). Touch devices continue
    // to use the existing tap/click toggle, and won't fire phantom hovers.
    this.supportsHover = window.matchMedia(
      '(hover: hover) and (pointer: fine)'
    ).matches;

    this.init();
  }

  init() {
    this.cacheDomReferences().createOverlay().setupHandlers().enable();

    return this;
  }

  cacheDomReferences() {
    this.menuToggle = this.element.querySelector(SELECTORS.MOBILE_MENU_TOGGLE);
    this.mobileMenu = this.element.querySelector(SELECTORS.MOBILE_MENU);
    this.mobileHeader = this.element.querySelector(SELECTORS.MOBILE_HEADER);
    this.subToggles = this.element.querySelectorAll(
      SELECTORS.MOBILE_MENU_SUB_TOGGLE
    );
    this.searchToggle = this.element.querySelector(
      SELECTORS.MOBILE_SEARCH_TOGGLE
    );
    this.searchPanel = this.element.querySelector(SELECTORS.MOBILE_SEARCH);
    this.searchInput = this.element.querySelector(
      SELECTORS.MOBILE_SEARCH_INPUT
    );
    this.searchBack = this.element.querySelector(SELECTORS.MOBILE_SEARCH_BACK);
    this.searchSuggestions = this.element.querySelector(
      SELECTORS.MOBILE_SEARCH_SUGGESTIONS
    );
    this.searchSuggestionsList = this.element.querySelector(
      SELECTORS.MOBILE_SEARCH_SUGGESTIONS_LIST
    );
    this.searchResultsList = this.element.querySelector(
      SELECTORS.MOBILE_SEARCH_RESULTS_LIST
    );
    this.desktopToggles = this.element.querySelectorAll(
      SELECTORS.DESKTOP_HEADER_TOGGLE
    );
    // The parent <li> for each desktop toggle. These are the elements we
    // attach hover listeners to, so that hovering anywhere over the item
    // (toggle or the dropdown panel, which is a DOM child of the item)
    // keeps the dropdown open.
    this.desktopItems = Array.from(this.desktopToggles)
      .map((toggle) => toggle.closest(SELECTORS.DESKTOP_HEADER_ITEM))
      .filter(Boolean);

    return this;
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.classList.add(CLASS_NAMES.MOBILE_MENU_OVERLAY);
    document.body.appendChild(this.overlay);

    return this;
  }

  setupHandlers() {
    this.handleMenuToggleClick = this.onMenuToggleClick.bind(this);
    this.handleSubToggleClick = this.onSubToggleClick.bind(this);
    this.handleOverlayClick = this.onOverlayClick.bind(this);
    this.handleDocumentClick = this.onDocumentClick.bind(this);
    this.handleDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.handleSearchBackClick = this.onSearchBackClick.bind(this);
    this.handleSearchInput = this.onSearchInput.bind(this);
    this.handleDesktopToggleClick = this.onDesktopToggleClick.bind(this);
    this.handleDesktopItemMouseEnter = this.onDesktopItemMouseEnter.bind(this);
    this.handleDesktopItemMouseLeave = this.onDesktopItemMouseLeave.bind(this);

    return this;
  }

  enable() {
    if (this.menuToggle) {
      this.menuToggle.addEventListener(
        EVENTS.CLICK,
        this.handleMenuToggleClick
      );
    }

    if (this.searchBack) {
      this.searchBack.addEventListener(
        EVENTS.CLICK,
        this.handleSearchBackClick
      );
    }

    if (this.searchInput) {
      this.searchInput.addEventListener(EVENTS.INPUT, this.handleSearchInput);
    }

    this.subToggles.forEach((toggle) => {
      toggle.addEventListener(EVENTS.CLICK, this.handleSubToggleClick);
    });

    this.desktopToggles.forEach((toggle) => {
      toggle.addEventListener(EVENTS.CLICK, this.handleDesktopToggleClick);
    });

    // Desktop-only enhancement: open the relevant dropdown on hover.
    // The click toggle above is left intact so keyboard (Enter/Space) and
    // touch interactions keep working exactly as before.
    if (this.supportsHover) {
      this.desktopItems.forEach((item) => {
        item.addEventListener('mouseenter', this.handleDesktopItemMouseEnter);
        item.addEventListener('mouseleave', this.handleDesktopItemMouseLeave);
      });
    }

    this.overlay.addEventListener(EVENTS.CLICK, this.handleOverlayClick);
    document.addEventListener(EVENTS.CLICK, this.handleDocumentClick);
    document.addEventListener(EVENTS.KEY_DOWN, this.handleDocumentKeydown);

    return this;
  }

  /**
   * Locks background scrolling while the mobile menu / search is open.
   * Stores the current scroll position and pins the body so the page
   * cannot scroll behind the menu (works on iOS Safari as well).
   * @returns {this} returns A reference to the current instance of the class
   */
  lockScroll() {
    if (document.body.classList.contains(CLASS_NAMES.MENU_OPEN)) return this;

    this.scrollPosition = window.scrollY || window.pageYOffset;
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.classList.add(CLASS_NAMES.MENU_OPEN);

    return this;
  }

  /**
   * Unlocks background scrolling and restores the scroll position
   * the user was at before the menu was opened.
   * @returns {this} returns A reference to the current instance of the class
   */
  unlockScroll() {
    if (!document.body.classList.contains(CLASS_NAMES.MENU_OPEN)) return this;

    document.body.classList.remove(CLASS_NAMES.MENU_OPEN);
    document.body.style.top = '';
    window.scrollTo(0, this.scrollPosition);

    return this;
  }

  onMenuToggleClick(event) {
    event.stopPropagation();

    const isOpen = this.mobileMenu.classList.contains(CLASS_NAMES.IS_OPEN);

    if (isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }

    return this;
  }

  onSubToggleClick(event) {
    event.stopPropagation();

    const toggle = event.currentTarget;
    const dropdown = toggle.nextElementSibling;

    // Read the open state straight from the DOM: the dropdown is open
    // when its `hidden` attribute has been removed. This avoids any
    // mismatch with ARIA attribute string comparisons.
    const isOpen = dropdown && !dropdown.hasAttribute('hidden');

    // Toggle behaviour: clicking an already-open item closes it again.
    if (isOpen) {
      this.closeSubMenu(toggle, dropdown);

      return this;
    }

    // Accordion behaviour: only one dropdown can be open at a time,
    // so close every other submenu before opening the clicked one.
    this.closeAllSubMenus();
    this.openSubMenu(toggle, dropdown);

    return this;
  }

  onOverlayClick() {
    this.closeMenu();
    this.closeSearch();

    return this;
  }

  onDocumentClick(event) {
    const clickedInsideHeader = event.target.closest(
      SELECTORS.HEADER_COMPONENT
    );

    if (!clickedInsideHeader) {
      this.closeAllDesktopDropdowns();
    }

    return this;
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Escape' && event.keyCode !== KEY_CODES.ESCAPE) {
      return this;
    }

    this.closeMenu();
    this.closeSearch();
    this.closeAllDesktopDropdowns();

    return this;
  }

  onSearchToggleClick(event) {
    event.stopPropagation();

    const isOpen = this.searchPanel.classList.contains(CLASS_NAMES.IS_OPEN);

    if (isOpen) {
      this.closeSearch();
    } else {
      this.openSearch();
    }

    return this;
  }

  onSearchBackClick() {
    this.closeSearch();

    return this;
  }

  onSearchInput() {
    clearTimeout(this.searchDebounceTimer);

    const query = this.searchInput.value.trim();

    if (!query) {
      this.hideSuggestions();
      this.clearResults();

      return this;
    }

    this.searchDebounceTimer = setTimeout(() => {
      this.fetchSuggestions(query);
      this.fetchResults(query);
    }, 300);

    return this;
  }

  onDesktopToggleClick(event) {
    event.stopPropagation();

    const toggle = event.currentTarget;
    const item = toggle.closest(SELECTORS.DESKTOP_HEADER_ITEM);
    const isOpen = item.classList.contains(CLASS_NAMES.IS_OPEN);

    this.closeAllDesktopDropdowns();

    if (!isOpen) {
      this.openDesktopDropdown(item, toggle);
    }

    return this;
  }

  /**
   * Opens the hovered item's dropdown (desktop, hover-capable pointers only).
   * Cancels any pending close so moving between the toggle and the dropdown
   * panel doesn't briefly close it. Only one dropdown is open at a time.
   * @param {MouseEvent} event The mouseenter event on the desktop item
   * @returns {this} returns A reference to the current instance of the class
   */
  onDesktopItemMouseEnter(event) {
    const item = event.currentTarget;
    const toggle = item.querySelector(SELECTORS.DESKTOP_HEADER_TOGGLE);

    if (!toggle) return this;

    clearTimeout(this.dropdownCloseTimer);

    // Already open (e.g. re-entering via the dropdown panel) — nothing to do.
    if (item.classList.contains(CLASS_NAMES.IS_OPEN)) return this;

    this.closeAllDesktopDropdowns();
    this.openDesktopDropdown(item, toggle);

    return this;
  }

  /**
   * Schedules the open dropdown to close shortly after the pointer leaves
   * the item. The small delay is the "hover intent" buffer that lets the
   * cursor cross the visual gap between the toggle and its dropdown panel.
   * @returns {this} returns A reference to the current instance of the class
   */
  onDesktopItemMouseLeave() {
    clearTimeout(this.dropdownCloseTimer);

    this.dropdownCloseTimer = setTimeout(() => {
      this.closeAllDesktopDropdowns();
    }, HeaderNavigation.DROPDOWN_CLOSE_DELAY_MS);

    return this;
  }

  fetchSuggestions(query) {
    const url = `/wp-json/wp/v2/search?search=${encodeURIComponent(
      query
    )}&per_page=5&subtype=any`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => this.renderSuggestions(data, query))
      .catch(() => this.hideSuggestions());

    return this;
  }

  fetchResults(query) {
    const url = `/wp-json/wp/v2/search?search=${encodeURIComponent(
      query
    )}&per_page=10&subtype=any`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => this.renderResults(data))
      .catch(() => this.clearResults());

    return this;
  }

  renderSuggestions(data, query) {
    if (!data.length) {
      this.hideSuggestions();

      return this;
    }

    this.searchSuggestionsList.innerHTML = data
      .map((item) => {
        const isActive = item.title.toLowerCase() === query.toLowerCase();

        return `
          <li class="mobile-search__suggestions-item${
            isActive ? ` ${CLASS_NAMES.IS_ACTIVE}` : ''
          }" role="option">
            <span class="mobile-search__suggestions-text">${item.title}</span>
          </li>
        `;
      })
      .join('');

    this.searchSuggestionsList
      .querySelectorAll('.mobile-search__suggestions-item')
      .forEach((el, i) => {
        el.addEventListener(EVENTS.CLICK, () => {
          this.searchInput.value = data[i].title;
          this.hideSuggestions();
          this.fetchResults(data[i].title);
        });
      });

    this.showSuggestions();

    return this;
  }

  renderResults(data) {
    if (!data.length) {
      this.clearResults();

      return this;
    }

    this.searchResultsList.innerHTML = data
      .map(
        (item) => `
          <li class="mobile-search__result-item">
            <a class="mobile-search__result-link" href="${item.url}">
              <div>
                ${
                  item.subtype
                    ? `<span class="mobile-search__result-category">${item.subtype}</span>`
                    : ''
                }
                <span class="mobile-search__result-title">${item.title}</span>
              </div>
              <div class="mobile-search__result-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
              </div>
            </a>
          </li>
        `
      )
      .join('');

    return this;
  }

  showSuggestions() {
    this.searchSuggestions.removeAttribute('hidden');

    return this;
  }

  hideSuggestions() {
    this.searchSuggestions.setAttribute('hidden', '');
    this.searchSuggestionsList.innerHTML = '';

    return this;
  }

  clearResults() {
    this.searchResultsList.innerHTML = '';

    return this;
  }

  /**
   * Hover-intent buffer before a dropdown starts closing. Long enough that
   * clipping the edge of the toggle, or arcing towards the link in the top
   * left of the panel, does not flash the menu shut.
   * @returns {number} Delay in milliseconds.
   */
  static get DROPDOWN_CLOSE_DELAY_MS() {
    return 300;
  }

  /**
   * Fade length. Must match the dropdown transition in _header.scss: the
   * `hidden` attribute is only re-applied once the fade has finished, because
   * `hidden` sets `display: none` and would otherwise cut the fade short.
   * @returns {number} Duration in milliseconds.
   */
  static get DROPDOWN_FADE_MS() {
    return 250;
  }

  /**
   * Forces a style recalculation so a just-unhidden element transitions from
   * its starting state rather than jumping to the end state.
   * @param {HTMLElement} element Element about to be transitioned.
   * @returns {this} returns A reference to the current instance of the class
   */
  forceReflow(element) {
    // The return value is deliberately unused; reading the property is what
    // flushes pending style changes.
    element.getBoundingClientRect();

    return this;
  }

  /**
   * Applies `hidden` to every dropdown that is not currently open.
   *
   * Used when switching straight from one menu to another: the outgoing panel
   * must be hidden at once rather than left mid-fade, since it is a full-width
   * absolutely positioned element that would otherwise sit invisibly over the
   * page and swallow clicks.
   * @returns {this} returns A reference to the current instance of the class
   */
  hideInactiveDropdowns() {
    this.desktopToggles.forEach((toggle) => {
      const item = toggle.closest(SELECTORS.DESKTOP_HEADER_ITEM);
      const dropdown = item?.querySelector(SELECTORS.DESKTOP_HEADER_DROPDOWN);

      if (dropdown && !item.classList.contains(CLASS_NAMES.IS_OPEN)) {
        dropdown.setAttribute('hidden', '');
      }
    });

    return this;
  }

  openDesktopDropdown(item, toggle) {
    const dropdown = item.querySelector(SELECTORS.DESKTOP_HEADER_DROPDOWN);

    // A fade-out may still be pending from the menu being replaced. Flush it
    // rather than cancel it, so no faded-out panel is left covering the page.
    clearTimeout(this.dropdownHideTimer);
    this.hideInactiveDropdowns();

    toggle.setAttribute(ARIA.EXPANDED, ARIA.TRUE);
    dropdown?.removeAttribute('hidden');

    // Read back a layout value so the browser paints the hidden state before
    // `is-open` flips it — without this the element goes straight to its final
    // opacity and no transition runs.
    if (dropdown) this.forceReflow(dropdown);

    item.classList.add(CLASS_NAMES.IS_OPEN);

    return this;
  }

  closeAllDesktopDropdowns() {
    // Cancel any pending hover-close so it can't fire after the dropdowns
    // have already been closed by another interaction.
    clearTimeout(this.dropdownCloseTimer);
    clearTimeout(this.dropdownHideTimer);

    const closing = [];

    this.desktopToggles.forEach((toggle) => {
      const item = toggle.closest(SELECTORS.DESKTOP_HEADER_ITEM);
      const dropdown = item?.querySelector(SELECTORS.DESKTOP_HEADER_DROPDOWN);

      item?.classList.remove(CLASS_NAMES.IS_OPEN);
      toggle.setAttribute(ARIA.EXPANDED, ARIA.FALSE);

      if (item && dropdown) closing.push({ item, dropdown });
    });

    // Let the fade play out, then take the panels out of the accessibility
    // tree. Re-opening in the meantime clears this timer, and the guard stops
    // a panel that has been re-opened from being hidden underneath itself.
    this.dropdownHideTimer = setTimeout(() => {
      closing.forEach(({ item, dropdown }) => {
        if (!item.classList.contains(CLASS_NAMES.IS_OPEN)) {
          dropdown.setAttribute('hidden', '');
        }
      });
    }, HeaderNavigation.DROPDOWN_FADE_MS);

    return this;
  }

  openSearch() {
    this.closeMenu();
    this.searchPanel.classList.add(CLASS_NAMES.IS_OPEN);
    this.searchPanel.removeAttribute('hidden');
    this.searchPanel.setAttribute(ARIA.HIDDEN, ARIA.FALSE);
    this.searchToggle?.setAttribute(ARIA.EXPANDED, ARIA.TRUE);
    this.lockScroll();

    setTimeout(() => this.searchInput?.focus(), 50);

    return this;
  }

  closeSearch() {
    if (!this.searchPanel) return this;

    this.searchPanel.classList.remove(CLASS_NAMES.IS_OPEN);
    this.searchPanel.setAttribute('hidden', '');
    this.searchPanel.setAttribute(ARIA.HIDDEN, ARIA.TRUE);
    this.searchToggle?.setAttribute(ARIA.EXPANDED, ARIA.FALSE);
    this.unlockScroll();
    this.hideSuggestions();
    this.clearResults();

    if (this.searchInput) {
      this.searchInput.value = '';
    }

    return this;
  }

  openMenu() {
    this.mobileHeader.classList.add(CLASS_NAMES.IS_OPEN);
    this.mobileMenu.classList.add(CLASS_NAMES.IS_OPEN);
    this.mobileMenu.removeAttribute('hidden');
    this.mobileMenu.setAttribute(ARIA.HIDDEN, ARIA.FALSE);
    this.menuToggle?.setAttribute(ARIA.EXPANDED, ARIA.TRUE);
    this.overlay.classList.add(CLASS_NAMES.IS_ACTIVE);
    this.lockScroll();

    return this;
  }

  closeMenu() {
    if (!this.mobileMenu) return this;

    this.mobileHeader.classList.remove(CLASS_NAMES.IS_OPEN);
    this.mobileMenu.classList.remove(CLASS_NAMES.IS_OPEN);
    this.mobileMenu.setAttribute('hidden', '');
    this.mobileMenu.setAttribute(ARIA.HIDDEN, ARIA.TRUE);
    this.menuToggle?.setAttribute(ARIA.EXPANDED, ARIA.FALSE);
    this.overlay.classList.remove(CLASS_NAMES.IS_ACTIVE);
    this.unlockScroll();

    this.closeAllSubMenus();

    return this;
  }

  openSubMenu(toggle, dropdown) {
    toggle.setAttribute(ARIA.EXPANDED, ARIA.TRUE);
    dropdown?.removeAttribute('hidden');

    return this;
  }

  closeSubMenu(toggle, dropdown) {
    toggle.setAttribute(ARIA.EXPANDED, ARIA.FALSE);
    dropdown?.setAttribute('hidden', '');

    return this;
  }

  closeAllSubMenus() {
    this.subToggles.forEach((toggle) => {
      const dropdown = toggle.nextElementSibling;
      this.closeSubMenu(toggle, dropdown);
    });

    return this;
  }
}
